import React, { useMemo } from 'react'
import { ListedItemDetailsComponentProps } from './listed-item-details-component.types'
import { useListedItemDetailsComponentStyles } from './listed-item-details-component.styles'
import Grid from '@material-ui/core/Grid'
import { ItemImageSlider } from '../../../shared/item-image-slider'
import { ShareMenu, ShareMenuOption } from '../../../shared/share-menu'
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined'
import { InfoComponent } from './info-component'
import { EmptyImageComponent } from './empty-image-component'
import Skeleton from '@material-ui/lab/Skeleton'
import Box from '@material-ui/core/Box'
import { useTranslation } from 'react-i18next'
import {
  buildListedItemShareMailtoHref,
  canShareListedItemByEmail,
} from '../../../utils/share-by-email'

export const ListedItemDetailsComponent: React.FC<ListedItemDetailsComponentProps> =
  (props: ListedItemDetailsComponentProps) => {
    const {
      isCurrentUserOwner,
      images,
      itemCondition,
      currency,
      description,
      locale,
      currentPrice,
      name,
      nonprofit,
      nonprofitName,
      donorName,
      listedUtc,
      createdUtc,
      soldUtc,
      state,
      listedItemId,
      loading,
      city,
      region,
      deliveryType,
      isLocalPickupAvailable,
      isShippingAvailable,
      setInStoreDeliveryType,
    } = props

    const classes = useListedItemDetailsComponentStyles()
    const { t } = useTranslation()

    const shareByEmailHref = useMemo(() => {
      if (
        !canShareListedItemByEmail(loading, state, listedItemId, name)
      ) {
        return undefined
      }
      return buildListedItemShareMailtoHref(t, {
        listedItemId: listedItemId!,
        name: name!,
        nonprofitName,
        senderName: donorName,
      })
    }, [loading, state, listedItemId, name, nonprofitName, donorName, t])

    const shareMenuOptions = useMemo((): ShareMenuOption[] => {
      if (!shareByEmailHref) {
        return []
      }
      return [
        {
          id: 'email',
          label: t('itemDetails.shareMenu.email'),
          icon: <EmailOutlinedIcon fontSize="small" />,
          href: shareByEmailHref,
        },
      ]
    }, [shareByEmailHref, t])

    //Placeholder
    const placeholderBlock = () => (
      <div>
        <Box pt={0.5}>
          <Skeleton className={classes.imgSkeleton} animation="wave" />
        </Box>
      </div>
    )

    //Img Block
    const imgBlock = () =>
      images && images.length ? (
        <ItemImageSlider images={images} variant="public" />
      ) : (
        <EmptyImageComponent />
      )

    const imageColumn = () => (
      <>
        {loading ? placeholderBlock() : imgBlock()}
        <ShareMenu
          label={t('itemDetails.shareMenu.label')}
          options={shareMenuOptions}
        />
      </>
    )

    return (
      <div className={classes.wrapper}>
        <div className={classes.sliderBlock}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={7} className={classes.sliderSectionBig}>
              {imageColumn()}
            </Grid>
            <Grid item xs={12} md={5}>
              <InfoComponent
                listedItemId={listedItemId}
                deliveryType={deliveryType}
                isCurrentUserOwner={isCurrentUserOwner}
                itemCondition={itemCondition}
                currency={currency}
                city={city}
                region={region}
                description={description}
                locale={locale}
                loading={loading}
                name={name}
                state={state}
                listedUtc={listedUtc}
                createdUtc={createdUtc}
                soldUtc={soldUtc}
                nonprofitName={nonprofitName}
                externalId={nonprofit.externalId ? nonprofit.externalId : '0'}
                donorName={donorName}
                isLocalPickupAvailable={isLocalPickupAvailable}
                isShippingAvailable={isShippingAvailable}
                setInStoreDeliveryType={setInStoreDeliveryType}
                currentPrice={currentPrice}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    )
  }
